import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';

/**
 * useNotificationSocket — connects to /api/ws/notifications for push alerts.
 * Uses a connection ID to prevent stale async attempts from creating orphan sockets.
 */
export const useNotificationSocket = (onNotification) => {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const connectionId = useRef(0);
  const [connected, setConnected] = useState(false);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const connect = useCallback(async (connId) => {
    if (connId !== connectionId.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    let token;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/ws-token`, { withCredentials: true });
      token = data.token;
    } catch (err) {
      console.error('Notification WS token error:', err.message);
      return;
    }
    if (!token || connId !== connectionId.current) return;

    const wsBase = BACKEND_URL || window.location.origin;
    const wsUrl = wsBase.replace(/^http/, 'ws') + '/api/ws/notifications';
    const ws = new WebSocket(wsUrl);

    if (connId !== connectionId.current) {
      ws.onopen = () => ws.close();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      if (connId !== connectionId.current) { ws.close(); return; }
      setConnected(true);
      ws.send(JSON.stringify({ token }));
    };

    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (onNotificationRef.current) onNotificationRef.current(data);
      } catch (err) {
        console.error('Notification WS parse error:', err.message);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (connId === connectionId.current) {
        reconnectTimer.current = setTimeout(() => connect(connId), 5000);
      }
    };

    ws.onerror = () => { ws.close(); };
  }, []);

  useEffect(() => {
    const connId = ++connectionId.current;
    connect(connId);
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 45000);

    return () => {
      connectionId.current = connId + 1;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { connected };
};
